import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { dollars } from "@/lib/giftIcons";
import { Radio, MapPin, BadgeCheck } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { VellutoAddress, FindByAddress } from "@/components/VellutoAddress";
import { CrownRace } from "@/components/CrownRace";
import { ReferralBoard } from "@/components/ReferralBoard";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1637059880830-59a90102de77";

function ModelCard({ m, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08 }}
      data-testid={`model-card-${m.id}`}
      className="group relative rounded-2xl border border-white/8 bg-surface overflow-hidden"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={m.photo_url || FALLBACK_IMG}
          alt={m.display_name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
        {m.streaming_live && (
          <span data-testid={`live-badge-${m.id}`} className="absolute top-4 left-4 z-10 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white bg-red-600 px-3 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live now
          </span>
        )}
        {m.private_available && m.private_rate >= 299 && (
          <span className="absolute top-4 right-4 z-10 text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-3 py-1 rounded-full backdrop-blur-sm">
            {dollars(m.private_rate)}/min
          </span>
        )}
      </div>
      <div className="relative z-10 p-5 -mt-16">
        <h3 className="font-display text-2xl leading-none flex items-center gap-2">
          {m.display_name}
          {m.online && <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" title="Online now" />}
          {m.verified && <BadgeCheck size={18} className="text-emerald-400" />}
          <VerifiedBadge user={m} size="xs" testId={`model-id-verified-${m.id}`} />
        </h3>
        <VellutoAddress user={m} testId={`model-address-${m.id}`} className="mt-1.5" />
        {m.location && (
          <p className="flex items-center gap-1 text-xs text-text-muted mt-2">
            <MapPin size={12} /> {m.location}
          </p>
        )}
        {m.tagline && <p className="text-sm text-text-main/80 mt-3 italic font-display text-lg">"{m.tagline}"</p>}
        <Link
          to={`/models/${m.id}/live`}
          data-testid={`enter-live-${m.id}`}
          className={`mt-5 flex items-center justify-center gap-2 text-center text-xs uppercase tracking-[0.2em] py-3 rounded-full transition-colors ${
            m.streaming_live ? "bg-wine hover:bg-wine-hover" : "border border-white/10 hover:border-white/30 hover:bg-white/5"
          }`}
        >
          <Radio size={13} /> {m.streaming_live ? "Join live room" : "Visit room"}
        </Link>
      </div>
    </motion.div>
  );
}

export default function Models() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/models").then(({ data }) => setModels(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Velluto Models</p>
        <h1 className="font-display text-6xl font-light mb-4">Live cam chat</h1>
        <p className="text-text-muted max-w-lg mb-6">
          Watch, chat, and gift your favorite creators live — or step into a private 1-on-1 cam-to-cam session.
        </p>
        <div className="mb-10 grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr] items-start">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted mb-2">Find a creator by address</p>
            <FindByAddress />
          </div>
          <div className="space-y-6"><CrownRace /><ReferralBoard /></div>
        </div>
      </motion.div>

      {loading ? (
        <p className="text-text-muted">Loading models…</p>
      ) : models.length === 0 ? (
        <p data-testid="no-models" className="text-text-muted font-display text-2xl italic">
          No models registered yet — be the first.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {models.map((m, i) => <ModelCard key={m.id} m={m} index={i} />)}
        </div>
      )}
    </div>
  );
}
